import { displayName } from "./analiseData";
import { normalizeReport } from "./revisorData";

/**
 * Dados do dashboard de um fornecedor dentro de uma análise do Revisor.
 * Regras vindas dos dados reais (Jun/26, 2026-11-04):
 * - os números do fornecedor vêm de `suppliers[]` em /details: encontradas e
 *   faltantes são a referência da tela. `percentual` passa de 100% e não é usado;
 * - /suppliers/:code/details devolve listas que NÃO fecham com esses números, e
 *   `xmls.faltantes` veio vazio. A API não diz quais documentos são os faltantes;
 * - `sobras` não são faltantes: das 31 testadas, 28 tinham XML. A tela as chama de
 *   "Não conferidos" e não trata a lista como pendência;
 * - nos documentos, `emitente.cnpj` traz o estabelecimento do grupo, não o
 *   emitente, e `valor` vem zerado (por isso a tela não mostra valor por documento);
 * - `data` pode vir vazia; o mês de emissão sai da chave de acesso (posições 3 a 6);
 * - `totalValue` não tem unidade confirmada: só entra como participação.
 */

const onlyDigits = (v) => String(v || "").replace(/\D/g, "");
const rootOf = (cnpj) => { const d = onlyDigits(cnpj); return d.length === 14 ? d.slice(0, 8) : ""; };

export const coverage = (encontradas, faltantes) => {
  const total = (encontradas || 0) + (faltantes || 0);
  return total > 0 ? ((encontradas || 0) / total) * 100 : 0;
};

const rankBy = (docs, keyOf) => {
  const map = new Map();
  docs.forEach((d) => {
    const key = keyOf(d);
    if (!key) return;
    const row = map.get(key) || { key, conferidos: 0, sobras: 0, total: 0 };
    row[d.status === "sobra" ? "sobras" : "conferidos"] += 1;
    row.total += 1;
    map.set(key, row);
  });
  return [...map.values()];
};

// A origem do CFOP está no primeiro dígito
export const cfopScope = (cfop) => ({
  1: "Entrada no estado", 2: "Entrada de outro estado", 3: "Entrada do exterior",
  5: "Saída no estado", 6: "Saída para outro estado", 7: "Saída para o exterior",
}[String(cfop).charAt(0)] || "");

const monthFromKey = (chave) => {
  const d = onlyDigits(chave);
  return d.length === 44 ? `20${d.slice(2, 4)}-${d.slice(4, 6)}` : "";
};

const normalizeDoc = (x, status) => {
  const chave = x.chave || x.id || "";
  const parsed = x.data ? Date.parse(x.data) : NaN;
  const month = monthFromKey(chave);
  const cfop = x.cfop && x.cfop !== "N/A" ? String(x.cfop) : "";
  return {
    chave,
    status,
    tipo: x.tipo === "CTe" ? "CT-e" : "NF-e",
    numero: String(x.numeroNota || "").replace(/^0+(?=\d)/, ""),
    estabelecimento: onlyDigits(x.emitente?.cnpj),
    cfop,
    month,
    date: Number.isNaN(parsed) ? null : new Date(parsed),
    sortKey: Number.isNaN(parsed) ? Date.parse(`${month || "1970-01"}-01`) : parsed,
  };
};

export function buildDocuments(xmls) {
  const docs = [
    ...(xmls?.encontradas || []).map((x) => normalizeDoc(x, "conferido")),
    ...(xmls?.sobras || []).map((x) => normalizeDoc(x, "sobra")),
  ].sort((a, b) => b.sortKey - a.sortKey);
  return {
    docs,
    conferidos: docs.filter((d) => d.status === "conferido").length,
    sobras: docs.filter((d) => d.status === "sobra").length,
    byEst: rankBy(docs, (d) => d.estabelecimento).sort((a, b) => b.total - a.total),
    byCfop: rankBy(docs, (d) => d.cfop).sort((a, b) => b.total - a.total),
    byMonth: rankBy(docs, (d) => d.month).sort((a, b) => a.key.localeCompare(b.key)),
  };
}

export function buildFornecedor(raw, report, details) {
  const rep = normalizeReport(report);
  const encontradas = raw.encontradas || 0;
  const faltantes = raw.faltantes || 0;
  const total = encontradas + faltantes;
  const pct = coverage(encontradas, faltantes);
  const all = details?.suppliers || [];

  const pending = all.filter((s) => (s.faltantes || 0) > 0).sort((a, b) => b.faltantes - a.faltantes);
  const rank = faltantes > 0 ? pending.filter((s) => s.faltantes > faltantes).length + 1 : null;
  const missingTotal = all.reduce((sum, s) => sum + (s.faltantes || 0), 0);
  const clean = all.length - pending.length;

  const valueTotal = all.reduce((sum, s) => sum + (s.totalValue || 0), 0);
  const valueShare = valueTotal > 0 && raw.totalValue > 0 ? (raw.totalValue / valueTotal) * 100 : null;

  // Outros CNPJs da mesma raiz
  const root = rootOf(raw.cnpj);
  const siblings = root
    ? all.filter((s) => rootOf(s.cnpj) === root && s.code !== raw.code)
      .map((s) => ({ raw: s, code: s.code, cnpj: s.cnpj, faltantes: s.faltantes || 0, encontradas: s.encontradas || 0, pct: coverage(s.encontradas, s.faltantes) }))
      .sort((a, b) => b.faltantes - a.faltantes || a.pct - b.pct)
    : [];
  const groupMissing = faltantes + siblings.reduce((sum, s) => sum + s.faltantes, 0);

  const own = !!root && rep.files.some((f) => rootOf(f.cnpj) === root);

  return {
    rep,
    name: displayName(raw.name),
    cnpj: raw.cnpj,
    encontradas,
    faltantes,
    total,
    pct,
    deltaToReport: total > 0 ? pct - rep.pct : null,
    rank,
    pending: pending.map((s) => ({ code: s.code, faltantes: s.faltantes })),
    missingTotal,
    missingShare: missingTotal > 0 ? (faltantes / missingTotal) * 100 : 0,
    clean,
    valueShare,
    siblings,
    groupMissing,
    own,
  };
}

// Este fornecedor em cada análise do projeto, em ordem de período
export function buildHistory(raw, report, reports, detailsById) {
  const current = report.id || report._id;
  const digits = onlyDigits(raw.cnpj);
  return reports
    .filter((r) => !report.projeto || !r.projeto || r.projeto === report.projeto)
    .map(normalizeReport)
    .filter((r) => r.startKey && detailsById[r.id])
    .sort((a, b) => a.startKey.localeCompare(b.startKey))
    .map((r) => {
      const list = detailsById[r.id].suppliers || [];
      const s = list.find((x) => x.code === raw.code) || (digits ? list.find((x) => onlyDigits(x.cnpj) === digits) : null);
      const encontradas = s?.encontradas || 0;
      const faltantes = s?.faltantes || 0;
      return { key: r.startKey, id: r.id, present: !!s && encontradas + faltantes > 0, encontradas, faltantes, pct: coverage(encontradas, faltantes), current: r.id === current };
    });
}
